import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/site/Logo";

const links = [
  { href: "#about", label: "About", id: "about" },
  { href: "#services", label: "Services", id: "services" },
  { href: "#reach", label: "Global Reach", id: "reach" },
  { href: "#partners", label: "Partners", id: "partners" },
  { href: "#contact", label: "Contact", id: "contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled
          ? "bg-navy-base/75 backdrop-blur-md border-b border-navy-border"
          : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <button
          onClick={() => scrollTo("top")}
          className="cursor-pointer"
          data-testid="nav-brand"
        >
          <Logo size={38} />
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="group relative font-mono text-[11px] tracking-[0.28em] uppercase text-white/70 hover:text-white transition-colors duration-300"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
              <span className="absolute -bottom-2 left-0 h-px w-0 bg-gold group-hover:w-full transition-[width] duration-500" />
            </button>
          ))}
        </nav>

        <button
          onClick={() => scrollTo("contact")}
          className="hidden md:inline-flex btn-gold"
          data-testid="nav-cta"
        >
          Request Brief
        </button>

        <button
          className="md:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle navigation"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-navy-surface border-t border-navy-border"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => scrollTo(l.id)}
                  className="text-left font-mono text-[12px] tracking-[0.28em] uppercase text-white/80"
                  data-testid={`nav-mobile-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => scrollTo("contact")}
                className="btn-gold self-start"
                data-testid="nav-mobile-cta"
              >
                Request Brief
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
